// 统一网络请求封装（对齐 uniapp 版调用结构与返回结构：resolve 出 { data: 后端JSON }）
import axios from 'axios';
import config from '@/config';
import { getStorage, clearStorage } from '@/utils/storage';
import { Toast } from 'vant';

// 是否正在跳转登录页（防止重复跳转）
let isRedirectingToLogin = false;

/** 处理未授权：清空缓存并跳回门户页
 * @param {String} msg - 提示文案（可选），如「您的账号已在其他设备登录」，默认提示登录过期
 */
function handleUnauthorized(msg) {
    if (isRedirectingToLogin) return;
    isRedirectingToLogin = true;
    clearStorage();
    Toast({ message: msg || '登录已过期，请重新登录', duration: 2000 });
    setTimeout(() => {
        window.location.href = `${window.location.origin}/portal`;
        setTimeout(() => {
            isRedirectingToLogin = false;
        }, 1000);
    }, 1500);
}

/** 发送请求
 * @param {Object} options - 请求配置
 * @param {String} options.url - 接口地址（自动拼接 baseUrl）
 * @param {String} options.method - 请求方法 GET/POST
 * @param {Object} options.data - 请求数据
 * @param {Object} options.header - 附加请求头
 * @param {Boolean} options.needAuth - 是否携带 token，默认 true
 * @returns {Promise<{data:Object}>} 与 uniapp 版结构一致
 */
export function request(options = {}) {
    const {
        url,
        method = 'GET',
        data = {},
        header = {},
        needAuth = true,
    } = options;

    // 构建请求头
    const requestHeader = { 'Content-Type': 'application/json', ...header };
    if (needAuth) {
        const token = getStorage('token');
        if (token) requestHeader.Authorization = `Bearer ${token}`;
    }

    // GET 参数走 query，POST 走 body（对齐 uni.request 行为）
    const isGet = method.toUpperCase() === 'GET';
    return axios({
        url: url.startsWith('http') ? url : `${config.baseUrl}${url}`,
        method,
        [isGet ? 'params' : 'data']: data,
        headers: requestHeader,
        timeout: 60000,
    }).then(
        (res) => {
            const body = res.data;
            // 处理 401 未授权（后端业务层返回），透传后端提示（如他处登录）
            if (body && body.code === 401) {
                handleUnauthorized(body.message);
                const authErr = new Error(body.message || '登录已过期');
                authErr.isAuthError = true;
                return Promise.reject(authErr);
            }
            return { data: body };
        },
        // HTTP 状态码非 2xx 走此分支：后端鉴权中间件对过期/无效 token/他处登录返回 HTTP 401，需踢回登录页
        (err) => {
            if (err.response && err.response.status === 401) {
                handleUnauthorized(
                    err.response.data && err.response.data.message
                );
                // isAuthError 标记：调用方 catch 检测到后静默返回，避免再弹「网络错误」盖掉踢下线提示
                const authErr = new Error('登录已过期');
                authErr.isAuthError = true;
                return Promise.reject(authErr);
            }
            return Promise.reject(err);
        }
    );
}

/** GET 请求
 * @param {String} url - 接口地址
 * @param {Object} params - URL 查询参数
 * @param {Object} options - 其他配置
 */
export function get(url, params = {}, options = {}) {
    const queryString = Object.keys(params)
        .map(
            (key) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
        )
        .join('&');
    return request({
        url: queryString ? `${url}?${queryString}` : url,
        method: 'GET',
        ...options,
    });
}

/** POST 请求
 * @param {String} url - 接口地址
 * @param {Object} data - 请求体
 * @param {Object} options - 其他配置
 */
export function post(url, data = {}, options = {}) {
    return request({ url, method: 'POST', data, ...options });
}

/** 上传文件（multipart/form-data）
 * @param {String} url - 上传地址
 * @param {File} filePath - 文件对象（H5 环境直接传 File）
 * @param {String} name - 文件字段名，默认 'file'
 * @param {Object} formData - 附加表单数据
 * @param {Object} options - 其他配置
 */
export function uploadFile(
    url,
    filePath,
    name = 'file',
    formData = {},
    options = {}
) {
    const fd = new FormData();
    fd.append(name, filePath);
    Object.keys(formData).forEach((k) => fd.append(k, formData[k]));

    const header = {};
    if (options.needAuth !== false) {
        const token = getStorage('token');
        if (token) header.Authorization = `Bearer ${token}`;
    }

    return (
        axios
            .post(
                url.startsWith('http') ? url : `${config.baseUrl}${url}`,
                fd,
                {
                    headers: header,
                    timeout: 60000,
                }
            )
            .then((res) => {
                const data = res.data;
                // 处理 401 未授权（后端业务层返回），透传后端提示（如他处登录）
                if (data && data.code === 401) {
                    handleUnauthorized(data.message);
                    const authErr = new Error(data.message || '登录已过期');
                    authErr.isAuthError = true;
                    return Promise.reject(authErr);
                }
                return { data };
            })
            // HTTP 401 状态码走此分支，统一踢回登录页
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    handleUnauthorized(
                        err.response.data && err.response.data.message
                    );
                    // isAuthError 标记：调用方 catch 检测到后静默返回，避免再弹「网络错误」盖掉踢下线提示
                    const authErr = new Error('登录已过期');
                    authErr.isAuthError = true;
                    return Promise.reject(authErr);
                }
                return Promise.reject(err);
            })
    );
}

export default { request, get, post, uploadFile, handleUnauthorized };
